import { removeTypename, unwrapConnection } from 'src/app/shared/rxjs.pipes';
import { TimeEntryType } from './../../../../shared/modules/graphql/interfaces/time-entry.interfaces';
import { TimeEntryTypeService } from './../../../../shared/services/time-entry/time-entry-type/time-entry-type.service';
import { filter, switchMap } from 'rxjs/operators';
import { IApiBranch, IApiCompany, IApiContact, IApiInvestigationBillTo, IApiInvestigationBillToFilterType, IApiExpenseType, IApiInvoiceableTypeTypes, IApiInvoiceLine, IApiInvoiceItemInput, IApiTimeEntry, IApiExpense, IApiInvoiceFilterType, IApiInvoice, IApiExpenseItem, IApiTimeEntryType, IApiSageItemResponse } from './../../../../shared/modules/graphql/types/types';
import { LoaderService } from 'src/app/shared/modules/loader/loader.service';
import { NotificationsService } from 'src/app/shared/modules/notifications/notifications.service';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { IApiInvestigation } from "src/app/shared/modules/graphql/types/types";
import { ContactService, ExpenseItemService, InvestigationService, InvoiceService } from "src/app/shared/services";
import { FormExpenseType } from '../investigation-time-and-exp-modal-kendo/investigation-time-and-exp-modal-kendo.component';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { forkJoin, of } from 'rxjs';
import { CanInvoiceComponentDeactivate } from 'src/app/shared/route.guards';
import { getFullName } from 'src/app/shared/pipes/name.pipe';
import { InvoiceEntrySelection, parseInvoiceSelectionParam } from 'src/app/shared/helpers/invoice-navigation';
import { ISageContactSyncResult, ISageContactTarget, SageContactSelectionModalComponent } from 'src/app/components/shared/investigations/sage-contact-selection-modal/sage-contact-selection-modal.component';
import { ISageClassSyncResult, ISageClassTarget, SageClassSelectionModalComponent } from 'src/app/components/shared/investigations/sage-class-selection-modal/sage-class-selection-modal.component';

dayjs.extend(utc);

interface IQbInvoiceLine extends Partial<IApiInvoiceLine> {
  ExpenseType?: Partial<IApiExpenseType>;
  selected?: boolean;
  qbAccount?: string;
  sageId?: number;
  id?: string;
}

/** The invoices a split creates, calculated the way the API does it so the preview matches */
export interface IBillToSplit {
  columns: { name: string; sageId?: string; company: string; percentage: number }[];
  /** One per column (primary first): its lines and total, the primary one ends with the rounding adjustment */
  invoices: { lines: { description: string; quantity: number; rate: number; amount: number; lineAmount?: number }[]; total: number }[];
  /** Billed on the primary invoice so the invoices add up to the original total, 0 when none */
  adjustment: number;
  /** Total of the original invoice lines */
  total: number;
  percentageTotal: number;
  /** The split only makes sense when the percentages add up to 100 */
  isValid: boolean;
  error?: string;
}

const QUANTITY_SCALE = 10000;
const PERCENTAGE_SCALE = 100;
const FULL_PERCENTAGE = 100 * PERCENTAGE_SCALE;

/** Rounds half up, for non negative numbers */
const roundDiv = (numerator: number, denominator: number): number => Math.floor((numerator + denominator / 2) / denominator);

/** Line amount in cents as Sage computes it, quantity in 1/10000 */
const amountInCents = (quantityUnits: number, rateInCents: number): number => roundDiv(quantityUnits * rateInCents, QUANTITY_SCALE);

interface IQbInvoiceItemUsers {
  userId: string;
  invoiceableId: string;
}

@Component({
  selector: 'app-investigation-invoice',
  templateUrl: './investigation-invoice.component.html',
  styleUrls: ['./investigation-invoice.component.scss']
})
export class InvestigationInvoiceComponent implements OnInit, CanInvoiceComponentDeactivate {

  /** Filled from the route: the investigation (with only the entries to invoice) when creating, the invoice and its investigation when viewing or editing. */
  public data: { investigation?: IApiInvestigation; invoice?: IApiInvoice; disableEdit?: boolean } = {};
  public formExpenseType: typeof FormExpenseType = FormExpenseType;

  public invoiceLines: IQbInvoiceLine[] = [];
  private invoiceItems: IApiInvoiceItemInput[] = [];
  private invoiceItemUsers: IQbInvoiceItemUsers[] = [];
  public details = "";
  public invoiceNumber = "";

  public expenseItems: IApiExpenseItem[] = [];
  public timeEntryTypes: TimeEntryType[] = [];
  public invoices: IApiInvoice[] = [];
  public sageItems: IApiSageItemResponse[] = [];
  public isBillingInfoCollapsed = false;
  private initialState: string = null;
  private isFinished = false;

  public get total(): number {
    return this.invoiceLines.reduce((prev, curr) => {
      prev += curr.quantity * curr.rate;
      return prev;
    }, 0);
  }

  public get selectedLines(): IQbInvoiceLine[] {
    return this.invoiceLines.filter(({ selected }) => selected);
  }

  public get investigation(): IApiInvestigation {
    return this.data?.investigation || null;
  }

  /** Bill To rows of the investigation, the primary one included */
  public billTos: IApiInvestigationBillTo[] = [];
  /** Sending waits for these, the Sage checks need every Bill To */
  public billTosLoaded = false;

  private billToSplitKey: string = null;
  private billToSplitCache: IBillToSplit = null;

  /**
   * Split of the invoice lines between the Bill Tos, recalculated whenever a line or a percentage changes.
   * Cached per input so the template gets the same object until something changes.
   */
  public get billToSplit(): IBillToSplit {
    // only a new invoice is split, an existing one is already one share of a split
    if (this.data?.invoice || !this.additionalBillTos.length) return null;

    const key = JSON.stringify([
      this.invoiceLines.map(({ description, quantity, rate }) => [description, quantity, rate]),
      this.billTos.map(({ id, percentage, isPrimary, BillTo }) => [id, percentage, isPrimary, BillTo?.sageId])
    ]);
    if (key !== this.billToSplitKey) {
      this.billToSplitKey = key;
      this.billToSplitCache = this.buildBillToSplit();
    }
    return this.billToSplitCache;
  }

  /** Same rule as the API, on integers: quantity in 1/10000, money in cents, percentage in 1/100 of a percent */
  private buildBillToSplit(): IBillToSplit {
    // primary first, then the additional ones in the order they were saved
    const billTos = [this.primaryBillTo, ...this.additionalBillTos].filter(Boolean);
    const percentages = billTos.map(({ percentage }) => Math.round((Number(percentage) || 0) * PERCENTAGE_SCALE));
    const percentageTotal = percentages.reduce((sum, value) => sum + value, 0) / PERCENTAGE_SCALE;
    const columns = billTos.map(({ BillTo, BillToBranch }, index) => ({
      name: getFullName(BillTo),
      sageId: BillTo?.sageId,
      company: BillToBranch?.Company?.name || '',
      percentage: percentages[index] / PERCENTAGE_SCALE
    }));
    const invalid = (error?: string): IBillToSplit =>
      ({ columns, invoices: [], adjustment: 0, total: 0, percentageTotal, isValid: false, error });

    if (percentageTotal !== 100) return invalid();

    const invoices = columns.map(() => ({ lines: [], total: 0 }));
    let adjustmentInCents = 0;
    let totalInCents = 0;

    for (const { description, quantity, rate } of this.invoiceLines) {
      const quantityUnits = Math.round((Number(quantity) || 0) * QUANTITY_SCALE);
      const rateInCents = Math.round((Number(rate) || 0) * 100);
      if (quantityUnits < 0 || rateInCents < 0) return invalid('Invoice lines with a negative quantity or rate cannot be split.');

      const originalAmount = amountInCents(quantityUnits, rateInCents);
      // every Bill To but the primary gets its percentage of the quantity
      const quantities = percentages.map((percentage, index) => index === 0 ? 0 : roundDiv(quantityUnits * percentage, FULL_PERCENTAGE));
      const remainder = quantityUnits - quantities.reduce((sum, value) => sum + value, 0);
      const target = originalAmount - quantities.reduce((sum, value) => sum + amountInCents(value, rateInCents), 0);
      if (remainder < 0 || target < 0) return invalid('The primary Bill To percentage is too small to split these invoice lines.');

      // the primary gets the rest, trimmed so the invoices never add up to more than the original amount
      const largestQuantity = rateInCents > 0 ? Math.floor((target * QUANTITY_SCALE + 4999) / rateInCents) : remainder;
      quantities[0] = Math.min(remainder, largestQuantity);
      adjustmentInCents += target - amountInCents(quantities[0], rateInCents);
      totalInCents += originalAmount;

      quantities.forEach((units, index) => {
        const amount = amountInCents(units, rateInCents);
        invoices[index].lines.push({
          description, quantity: units / QUANTITY_SCALE, rate: rateInCents / 100, amount: amount / 100, lineAmount: originalAmount / 100
        });
        invoices[index].total += amount;
      });
    }

    if (adjustmentInCents > 0) {
      invoices[0].lines.push({ description: 'Rounding adjustment', quantity: 1, rate: adjustmentInCents / 100, amount: adjustmentInCents / 100 });
      invoices[0].total += adjustmentInCents;
    }

    return {
      columns,
      invoices: invoices.map(({ lines, total }) => ({ lines, total: total / 100 })),
      adjustment: adjustmentInCents / 100,
      total: totalInCents / 100,
      percentageTotal,
      isValid: true
    };
  }

  /** An invoice of a split keeps the lines and Bill To it was created with, only its number and details can change */
  public get isSplitInvoice(): boolean {
    return !!this.data?.invoice?.splitGroupId;
  }

  public get canEditLines(): boolean {
    return !this.data?.disableEdit && !this.isSplitInvoice;
  }

  /** Numbers of the other invoices created by the same split */
  public get siblingInvoiceNumbers(): string[] {
    return (this.data?.invoice?.SplitInvoices || [])
      .filter(({ id }) => id !== this.data.invoice.id)
      .map(({ invoiceNumber }) => invoiceNumber);
  }

  /** The API appends a letter per Bill To when splitting, so there are fewer characters left for the number */
  public get invoiceNumberMaxLength(): number {
    return !this.data?.invoice && this.additionalBillTos.length ? 15 : 16;
  }

  /** Letter the API adds to the invoice number of the Bill To at this position (primary first), A to Z */
  public billToLetter(index: number): string {
    return index >= 0 && index < 26 ? String.fromCharCode(65 + index) : '';
  }

  /** Next number after the highest one used with this NEFCO number, ignoring split letters and custom numbers */
  private getNextInvoiceSequence(nefcoNumber: string): number {
    const sequences = this.invoices
      .filter(({ invoiceNumber }) => invoiceNumber?.startsWith(nefcoNumber))
      .map(({ invoiceNumber }) => {
        const rest = invoiceNumber.slice(nefcoNumber.length);
        const numeric = rest.match(/^(\d+)([A-Z])?$/);
        if (numeric) return Number(numeric[1]);
        // old scheme used a single letter from the 10th invoice onward
        if (/^[A-Z]$/.test(rest)) return 10 + rest.charCodeAt(0) - 65;
        return null;
      })
      .filter((sequence) => sequence !== null);
    return sequences.length ? Math.max(...sequences) + 1 : 1;
  }

  /** The Bill To row flagged primary, it carries the share for the main Bill To */
  public get primaryBillTo(): IApiInvestigationBillTo {
    return this.billTos.find(({ isPrimary }) => isPrimary) || null;
  }

  public get additionalBillTos(): IApiInvestigationBillTo[] {
    return this.billTos.filter(({ isPrimary }) => !isPrimary);
  }

  /** An existing invoice keeps the Bill To it was created with */
  public get billToContact(): IApiContact {
    return (this.data?.invoice ? this.data.invoice.BillTo : this.investigation?.BillTo) || null;
  }

  public get billToBranch(): IApiBranch {
    return (this.data?.invoice ? this.data.invoice.BillToBranch : this.investigation?.BillToBranch) || null;
  }

  public get qbAccounts(): string[] {
    return [...new Set([
      ...this.timeEntryTypes,
      ...this.expenseItems
    ].map(({ qbAccount }) => qbAccount).filter(v => !!v).sort((a, b) => a < b ? -1 : 1)
    )];
  }

  constructor(
    private notificationService: NotificationsService,
    private invoiceService: InvoiceService,
    private investigationService: InvestigationService,
    private loader: LoaderService,
    private expenseItemService: ExpenseItemService,
    private timeTypes: TimeEntryTypeService,
    private dialogService: DialogService,
    private contactService: ContactService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  async ngOnInit() {
    if (!this.initData()) return;

    this.loadBillTos();
    this.loader.show$(forkJoin([
      this.timeTypes.get().pipe(unwrapConnection()),
      this.expenseItemService.get().pipe(unwrapConnection()),
      this.invoiceService.get([{ type: IApiInvoiceFilterType.Investigation, value: this.investigation?.id ? this.investigation?.id : null }], { limit: -1 }).pipe(
        unwrapConnection()
      ),
      this.timeTypes.getSageItemList(),
      this.investigation?.BillTo?.id ? this.contactService.getById(this.investigation.BillTo.id) : of(null)
    ])).subscribe(([timeEntryTypes, expenseItems, invoices, sageItems, contact]) => {
      this.timeEntryTypes = timeEntryTypes;
      this.expenseItems = expenseItems;
      this.invoices = invoices;
      this.sageItems = sageItems;
      if (this.investigation && contact) this.investigation.BillTo = contact;
      // Set default Invoice#
      const nefcoNumber = this.investigation?.nefcoNumber;
      if (nefcoNumber) this.invoiceNumber = `${nefcoNumber}${this.getNextInvoiceSequence(nefcoNumber)}`;
      if (!this.data.invoice && this.investigation) {
        // use this to filter out items that should not be added as invoice lines (already pending or invoiced)
        const itemFilter = ({ InvoiceItems }: any) => !InvoiceItems.length;
        const itemBuilder = <T>(invoiceableType: IApiInvoiceableTypeTypes, _data: T | any) => {
          // Build Item arry
          this.invoiceItems.push({
            invoiceableId: _data.id,
            invoiceableType
          });

          // Build User array
          this.invoiceItemUsers.push({
            userId: _data.User.id,
            invoiceableId: _data.id
          });

          return _data;
        };
        // Time Entries
        this.invoiceLines = this.investigation.TimeEntries
          .filter(itemFilter)
          .map(item => itemBuilder<IApiTimeEntry>(IApiInvoiceableTypeTypes.Hours, item))
          .map(({ id, hours, billingDescription, description, workday, Type, User }) => ({
            quantity: Type.isFlatRate ? 1 : hours,
            rate: this.findRate(Type),
            description: (`${billingDescription || description}`).slice(0, 4000),
            ExpenseType: { name: this.formExpenseType.HOURS },
            qbAccount: Type.qbAccount,
            sageId: this.timeEntryTypes?.find(type => type.id === Type.id)?.sageId,
            staffNames: `${User.firstName} ${User.lastName}`,
            createdAt: new Date(workday),
            id
          }));

        this.invoiceLines.push(
          ...(
            this.investigation?.TimeEntries
              ?.filter(itemFilter)
              ?.filter(item => item?.travelHours)
              ?.map(item => itemBuilder<IApiTimeEntry>(IApiInvoiceableTypeTypes.Hours, item))
              ?.map(({ id, travelHours, workday, Type, User }) => ({
                quantity: Type?.isFlatRate ? 1 : travelHours,
                rate: this.findRate(Type),
                description: 'Travel time',
                ExpenseType: { name: this.formExpenseType.HOURS },
                qbAccount: Type?.qbAccount,
                sageId: this.timeEntryTypes?.find(type => type.id === Type?.id)?.sageId,
                staffNames: `${User?.firstName || ''} ${User?.lastName || ''}`.trim(),
                createdAt: workday ? new Date(workday) : undefined,
                id
              })) ?? []
          )
        );
        // Mileage
        this.invoiceLines.push(
          ...[
            ...this.investigation.Mileage
          ].filter(itemFilter)
            .map(item => itemBuilder<IApiExpense>(IApiInvoiceableTypeTypes.Miles, item))
            .map(({ id, billableQuantity, description, expenseDate, ExpenseItem, User }) => {

              const foundRate = this.findRate(ExpenseItem);

              return ({
                quantity: ExpenseItem.isFlatRate ? 1 : (foundRate === 1 ? foundRate : billableQuantity),
                rate: (foundRate === 1 && !ExpenseItem.isFlatRate) ? billableQuantity : foundRate,
                description: (`${description}`).slice(0, 4000),
                ExpenseType: { name: this.formExpenseType.MILEAGE },
                qbAccount: ExpenseItem?.qbAccount,
                sageId: this.expenseItems?.find(type => type.id === ExpenseItem.id)?.sageId,
                staffNames: `${User.firstName} ${User.lastName}`,
                createdAt: new Date(expenseDate),
                id
              });
            })
        );

        // Expense
        this.invoiceLines.push(
          ...[
            ...this.investigation.Expenses
          ].filter(itemFilter)
            .map(item => itemBuilder<IApiExpense>(IApiInvoiceableTypeTypes.Expense, item))
            .map(({ id, billableQuantity, description, expenseDate, ExpenseItem, User }) => {

              const foundRate = this.findRate(ExpenseItem);

              return ({
                quantity: ExpenseItem.isFlatRate ? 1 : (foundRate === 1 ? foundRate : billableQuantity),
                rate: (foundRate === 1 && !ExpenseItem.isFlatRate) ? billableQuantity : foundRate,
                description: (`${description}`).slice(0, 4000),
                ExpenseType: { name: this.formExpenseType.EXPENSE },
                qbAccount: ExpenseItem?.qbAccount || '',
                sageId: this.expenseItems?.find(type => type.id === ExpenseItem.id)?.sageId,
                staffNames: `${User.firstName} ${User.lastName}`,
                createdAt: new Date(expenseDate),
                id
              });
            })
        );

        if (this.investigation.Company?.FlatRates.length) {

          const foundRisk = this.investigation.Company.FlatRates.find(obj => obj.Risk.id === this.investigation.RiskType.id);

          if (foundRisk) {
            this.invoiceLines.unshift(
              {
                quantity: 1,
                rate: foundRisk.value,
                qbAccount: 'A. Investigation',
                description: (`${foundRisk.Risk.name}`).slice(0, 4000),
                ExpenseType: { name: this.formExpenseType.HOURS },
                staffNames: '',
                createdAt: new Date()
              }
            );
          }
        }

        // Sort invoice lines
        this.sortInvoiceLines();

      }
      else if (this.data.invoice) {

        this.details = this.data.invoice.details ? this.data.invoice.details : '';
        this.invoiceNumber = this.data.invoice.invoiceNumber;
        // Format ExpenseType correctly & sort invoice lines by date
        this.invoiceLines = this.data.invoice.InvoiceLines.map((obj) => {
          return {
            ...obj,
            ExpenseType: { name: obj.expenseType },
            createdAt: new Date(obj?.createdAt),
            qbAccount: obj.qbAccount || (obj.sageId ? this.sageItems.find(item => item.id === obj.sageId)?.name : '')
          };
        });

      }
      else {
        this.invoiceLines = [{
          quantity: 1,
          rate: 1,
          description: "New Invoice Line Item",
          ExpenseType: { name: this.formExpenseType.EXPENSE },
          qbAccount: null,
          sageId: null,
          staffNames: '',
          createdAt: new Date()
        }];
      }
      this.initialState = this.currentState();
    });
  }

  private loadBillTos() {
    if (!this.investigation?.id) {
      this.billTosLoaded = true;
      return;
    }

    this.investigationService.getInvestigationBillTos([
      { type: IApiInvestigationBillToFilterType.InvestigationId, value: this.investigation.id }
    ], { limit: -1 }).pipe(removeTypename()).subscribe({
      next: (result) => {
        this.billTos = result?.edges?.map((edge) => edge?.node).filter(Boolean) || [];
        this.billTosLoaded = true;
      },
      // the Bill To cards are informational, a failed load must not block the invoice
      error: () => {
        this.billTos = [];
        this.billTosLoaded = true;
      }
    });
  }

  /** Builds `data` from the route. Returns false when the user was sent back because there is nothing to show. */
  private initData(): boolean {
    const { investigation, invoiceData } = this.route.snapshot.data;
    const query = this.route.snapshot.queryParamMap;

    if (invoiceData?.invoice) {
      this.data = {
        invoice: invoiceData.invoice,
        investigation: invoiceData.investigation,
        disableEdit: query.get('mode') === 'view'
      };
      return true;
    }

    return this.initCreateData(investigation, query);
  }

  private initCreateData(investigation: IApiInvestigation, query: ParamMap): boolean {
    const time = parseInvoiceSelectionParam(query.get('time'));
    const expenses = parseInvoiceSelectionParam(query.get('expenses'));
    const mileage = parseInvoiceSelectionParam(query.get('mileage'));

    if (!investigation?.id || (!time && !expenses && !mileage)) {
      this.notificationService.error("No entries were selected for this invoice. Please select the entries to invoice and try again.");
      this.returnToPreviousPage();
      return false;
    }

    const pick = <T extends { id?: string }>(items: T[] = [], selection: InvoiceEntrySelection | null): T[] => {
      if (!selection) return [];
      return selection === 'all' ? items : items.filter(({ id }) => selection.includes(id));
    };
    const TimeEntries = pick(investigation.TimeEntries, time);
    const Expenses = pick(investigation.Expenses, expenses);
    // Mileage follows the expense ids unless every mileage entry was asked for
    const Mileage = mileage === 'all' ? investigation.Mileage : pick(investigation.Mileage, expenses);

    // The link may be stale (opened later, or in another tab), so only entries that are still uninvoiced can be used
    const requestedIds = [time, expenses].flatMap((selection) => Array.isArray(selection) ? selection : []);
    const availableIds = new Set(
      [...TimeEntries, ...Expenses, ...Mileage].filter(({ InvoiceItems }: any) => !InvoiceItems?.length).map(({ id }) => id)
    );
    const unavailable = requestedIds.filter((id) => !availableIds.has(id));

    if (requestedIds.length && unavailable.length === requestedIds.length) {
      this.notificationService.error("The selected entries are no longer available to invoice. They may already have been invoiced.");
      this.returnToPreviousPage();
      return false;
    }
    if (unavailable.length) {
      this.notificationService.notify(
        `${unavailable.length} selected ${unavailable.length > 1 ? 'entries were' : 'entry was'} already invoiced and removed from this invoice.`,
        { icon: true, style: 'warning' }
      );
    }

    // shallow copy so the loaded investigation is not changed when the Bill To is refreshed below
    this.data = { investigation: { ...investigation, TimeEntries, Expenses, Mileage }, disableEdit: false };
    return true;
  }

  /** Back to the page the user came from, falling back to the investigation or the dashboard */
  public returnToPreviousPage() {
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    // only follow in-app urls
    if (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')) {
      return this.router.navigateByUrl(returnUrl);
    }
    return this.router.navigate(this.investigation?.id ? ['/investigations', this.investigation.id] : ['/']);
  }

  public cancel() {
    this.returnToPreviousPage();
  }

  private currentState(): string {
    return JSON.stringify({
      invoiceNumber: this.invoiceNumber,
      details: this.details,
      lines: this.invoiceLines.map(({ selected, ...line }) => line)
    });
  }

  /** Used by the route guard to confirm before leaving with edits that were not sent */
  public hasUnsavedChanges(): boolean {
    return !this.isFinished && !this.data?.disableEdit && this.initialState !== null && this.currentState() !== this.initialState;
  }

  private finish() {
    this.isFinished = true;
    this.returnToPreviousPage();
  }

  private findRate(item: IApiExpenseItem | IApiTimeEntryType | any) {
    const ruleOverride = this.investigation.Company?.BillingRules.find((obj) => (obj?.ExpenseItem?.id === item.id) || (obj?.TimeEntryType?.id === item.id));
    return !!ruleOverride ? ruleOverride.value : ('value' in item ? item.value : item.rate);
  }

  /** Contacts that still have to be linked to SAGE: the main Bill To, plus the additional ones when a new invoice is created */
  private get sageContactTargets(): ISageContactTarget[] {
    const candidates: ISageContactTarget[] = [
      { contact: this.investigation?.BillTo, branch: this.investigation?.BillToBranch, label: 'Bill To' },
      ...(this.data.invoice ? [] : this.additionalBillTos.map(({ BillTo, BillToBranch }) => (
        { contact: BillTo, branch: BillToBranch, label: 'Additional Bill To' }
      )))
    ];

    // a contact can be on more than one row, it is linked once and the first row (main Bill To first) decides the branch
    return candidates.filter(({ contact }, index) =>
      contact?.id && !contact.sageId && candidates.findIndex((other) => other.contact?.id === contact.id) === index
    );
  }

  /** Companies that still have to be linked to a SAGE class, same rows as the contacts */
  private get sageClassTargets(): ISageClassTarget[] {
    const companies: IApiCompany[] = [
      this.investigation?.BillToBranch?.Company,
      ...(this.data.invoice ? [] : this.additionalBillTos.map(({ BillToBranch }) => BillToBranch?.Company))
    ];

    return companies
      .filter((company, index) => company?.id && !company.sageKey && companies.findIndex((other) => other?.id === company.id) === index)
      .map((company) => ({ company }));
  }

  public openSageContactSelectionModal(targets: ISageContactTarget[]) {
    const dialog: DialogRef = this.dialogService.open({
      content: SageContactSelectionModalComponent,
      width: "60%",
      preventAction: (ev) => {
        return ev !== ("closed" as any);
      },
    });

    const dialogInstance = dialog.content.instance as SageContactSelectionModalComponent;
    dialogInstance.investigation = this.investigation;
    dialogInstance.targets = targets;
    dialog.result.subscribe((result: any) => {
      const res: ISageContactSyncResult = result;
      if (!res?.contacts?.length) return;

      // every copy of a linked contact gets its SAGE ids, the main Bill To and the additional rows
      const contacts = [this.investigation?.BillTo, ...this.billTos.map(({ BillTo }) => BillTo)];
      res.contacts.forEach(({ id, sageId, sageKey }) => {
        contacts.filter((contact) => contact?.id === id).forEach((contact) => {
          contact.sageId = sageId;
          contact.sageKey = sageKey;
        });
      });
      // carries on with the next check once every contact is linked
      if (res.complete) this.send();
    });
  }

  public openSageClassSelectionModal(targets: ISageClassTarget[]) {
    const dialog: DialogRef = this.dialogService.open({
      content: SageClassSelectionModalComponent,
      width: "60%",
      preventAction: (ev) => {
        return ev !== ("closed" as any);
      },
    });

    const dialogInstance = dialog.content.instance as SageClassSelectionModalComponent;
    dialogInstance.investigation = this.investigation;
    dialogInstance.targets = targets;
    dialog.result.subscribe((result: any) => {
      const res: ISageClassSyncResult = result;
      if (!res?.companies?.length) return;

      const companies = [this.investigation?.BillToBranch?.Company, ...this.billTos.map(({ BillToBranch }) => BillToBranch?.Company)];
      res.companies.forEach(({ id, sageKey }) => {
        companies.filter((company) => company?.id === id).forEach((company) => company.sageKey = sageKey);
      });
      if (res.complete) this.send();
    });
  }


  public send() {
    const contactTargets = this.sageContactTargets;
    if (contactTargets.length) {
      this.openSageContactSelectionModal(contactTargets);
      return;
    }
    const classTargets = this.sageClassTargets;
    if (classTargets.length) {
      this.openSageClassSelectionModal(classTargets);
      return;
    }
    if (this.invoiceLines?.find(line => !line.sageId)) {
      return this.notificationService.error('Sage item is missing from one or more line items');
    }
    if (!this.data.invoice) {

      const userIds = this.invoiceItemUsers.map(user => user.userId);
      const users = this.investigation ? this.investigation.InvestigationStaff.filter(user => userIds.includes(user.User.id)) : null;
      const formattedUsers = users ? users.map(({ User, Role }) => `${User.firstName} ${User.lastName} - ${Role.title}`) : [];

      this.notificationService.kendoConfirm("Really send this invoice data to Sage?").pipe(
        filter(v => !!v),
      ).subscribe(() => {
        this.loader.show$(
          this.invoiceService.add({
            details: this.details,
            invoiceNumber: this.invoiceNumber,
            InvestigationId: this.investigation ? this.investigation.id : null,
            InvoiceLines: this.invoiceLines.map(({ quantity, rate, description, ExpenseType: { name }, sageId, qbAccount, staffNames, createdAt }, index) => ({
              // For some reason, even though "typeof quantity" returns number, graphql thought values with decimal were strings
              quantity: parseFloat(quantity.toString()),
              rate,
              description,
              expenseType: name,
              sageId,
              qbAccount,
              displayOrder: index,
              staffNames,
              createdAt
            })),
            InvoiceItems: this.invoiceItems,
            memo: formattedUsers.join(', '),
            BillToBranchId: this.investigation.BillToBranch ? this.investigation.BillToBranch.id : null,
            BillToId: this.investigation.BillTo ? this.investigation.BillTo.id : null,
          }).pipe(
            this.notificationService.snackbarPipe(
              this.additionalBillTos.length
                ? `${this.billTos.length} invoices created. Check their Sage status in the invoice list.`
                : "Invoice successfully created!"
            ),
            this.notificationService.snackbarErrorPipe()
          )).subscribe(() => this.finish())
      });
    }
    else {
      this.invoiceService.update({
        id: this.data.invoice.id,
        details: this.details,
        invoiceNumber: this.invoiceNumber,
        // the API refuses lines for an invoice of a split
        InvoiceLines: this.isSplitInvoice ? undefined : this.invoiceLines.map(({ quantity, rate, description, expenseType, sageId, staffNames, createdAt, qbAccount }, index) => ({
          quantity,
          rate,
          description,
          expenseType,
          sageId,
          qbAccount,
          displayOrder: index,
          staffNames,
          createdAt
        }))
      }).pipe(
        this.notificationService.snackbarPipe("Invoice successfully modified!"),
        this.notificationService.snackbarErrorPipe()
      ).subscribe(() => this.finish());
    }
  }

  public combine() {
    const flatRate = this.selectedLines.find((line) => this.investigation?.Company?.FlatRates.find((rate) => rate?.Risk?.name === line?.description));

    const combined = this.selectedLines.reduce<IQbInvoiceLine>((prev, { description, rate, quantity, createdAt, sageId, qbAccount, staffNames, ExpenseType: { name } }) => {
      const prevStaffNames = prev.staffNames ? prev.staffNames.split(', ') : [];
      const nextStaffNames = staffNames.split(', ') || [];

      if (prev.description) description = "\n" + description;
      prev.description += description;
      // Default to flatRate if one exists in the list. Otherwise, use next rate.
      prev.rate = flatRate?.rate || rate;
      prev.quantity += quantity;
      prev.createdAt = createdAt;
      prev.ExpenseType.name = name;
      prev.sageId = sageId;
      prev.qbAccount = qbAccount;
      prev.staffNames = [...new Set([...prevStaffNames, ...nextStaffNames])].sort().join(', ');
      return prev;
    }, {
      rate: 0,
      description: "",
      quantity: 0,
      createdAt: null,
      sageId: null,
      qbAccount: "",
      staffNames: "",
      ExpenseType: {
        name: ""
      }
    });

    // put the combined line where the topmost selected line is
    const firstSelectedIndex = this.selectedLines.reduce((prev, curr) => {
      const idx = this.invoiceLines.indexOf(curr);
      return !prev || idx < +prev ? idx : prev;
    }, null) as number;

    // rebuild invoice lines, including combined, then filter out the selected now captured in the combined line
    this.invoiceLines = [
      ...this.invoiceLines.slice(0, firstSelectedIndex || 0),
      combined,
      ...this.invoiceLines.slice(firstSelectedIndex)
    ].filter(l => !this.selectedLines.includes(l));

    // Sort invoice lines
    this.sortInvoiceLines();
  }

  public newLine() {
    // NOTE: using spread assignment here specifically to get change detection to fire when appending an item; .push does not trigger it.
    this.invoiceLines = [
      ...this.invoiceLines,
      {
        quantity: 1,
        rate: 1,
        qbAccount: null,
        sageId: null,
        description: "New Invoice Line Item",
        ExpenseType: { name: this.formExpenseType.EXPENSE },
        staffNames: "",
        createdAt: new Date()
      }
    ];
  }

  public deleteLines() {
    this.invoiceItems = this.invoiceItems.filter(item => !this.selectedLines.find(_line => _line?.id === item?.invoiceableId));
    this.invoiceLines = this.invoiceLines.filter(line => !this.selectedLines.find(_line => _line === line));
    this.invoiceItemUsers = this.invoiceItemUsers.filter(item => !this.selectedLines.find(_line => _line?.id === item?.invoiceableId));
  }

  public get sameType(): boolean {
    const selectedTypes = this.selectedLines.map((obj) => obj.ExpenseType.name);
    const selectedSageIds = this.selectedLines.map((obj) => obj.sageId);
    return selectedTypes.every((val) => val === selectedTypes[0]) && selectedSageIds.every((val) => val === selectedSageIds[0]);
  }

  // Sort invoice lines by
  // Investigators > Hours / Mileage / Photos / Expense / Reports > Oldest to Newest
  private sortInvoiceLines() {

    // Sort the array by createdAt in ascending order
    this.invoiceLines.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    // Group by staffNames for the same createdAt
    let groupedExpenses = {};
    this.invoiceLines.forEach(expense => {
      let key = expense.createdAt + '-' + expense.staffNames;
      if (!groupedExpenses[key]) {
        groupedExpenses[key] = [];
      }
      groupedExpenses[key].push(expense);
    });

    // Sort grouped expenses by ExpenseType
    Object.keys(groupedExpenses).forEach(key => {
      groupedExpenses[key].sort((a, b) => {
        let typeOrder = { 'hours': 1, 'mileage': 2, 'expense': 3 };
        return typeOrder[a.ExpenseType.name] - typeOrder[b.ExpenseType.name];
      });
    });

    // Concatenate sorted grouped expenses with remaining expenses
    let result = Object.values(groupedExpenses).flatMap(group => group).concat(this.invoiceLines.filter(expense => {
      let key = expense.createdAt + '-' + expense.staffNames;
      return !groupedExpenses[key];
    }));

    result.sort((a: any, b: any) => {
      if (a.staffNames === "NEFCO-APPLICATION NEFCO-APPLICATION" && b.staffNames !== "NEFCO-APPLICATION NEFCO-APPLICATION") {
        return 1;
      } else if (a.staffNames !== "NEFCO-APPLICATION NEFCO-APPLICATION" && b.staffNames === "NEFCO-APPLICATION NEFCO-APPLICATION") {
        return -1;
      } else {
        return 0;
      }
    });
    this.invoiceLines = result;
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.invoiceLines, event.previousIndex, event.currentIndex);
  }

  sageItemChange(value, dataItem) {
    if (!dataItem) return;
    const list =
      dataItem?.ExpenseType?.name === 'hours'
        ? this.timeEntryTypes
        : this.expenseItems as [];
    dataItem.qbAccount =
      list?.find(item => item.sageId === value)?.qbAccount || '';
  }
}
